import { useEffect, useRef } from 'react';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
const DEFAULT_IDLE_TIMEOUT_MS = 5 * 60_000;
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'wheel', 'click', 'touchstart'];
const THROTTLE_MS = 1000;
/**
 * Runs a callback on a recurring interval, automatically pausing when:
 * - The browser tab is hidden (Page Visibility API)
 * - The user has been idle for `idleTimeoutMs` (no mouse, keyboard, wheel, click, or touch activity)
 *
 * When the tab becomes visible again or the user resumes activity, the callback
 * fires immediately and the interval restarts.
 *
 * @example
 * usePollingInterval(
 *   () => { request.retry(); },
 *   { intervalMs: 60_000 },
 * );
 */
export function usePollingInterval(callback, options) {
    const { intervalMs, idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS } = options;
    const stableCallback = useCallbackRef(callback);
    const intervalRef = useRef(null);
    const idleTimerRef = useRef(null);
    const isIdleRef = useRef(false);
    const lastActivityTimeRef = useRef(0);
    useEffect(() => {
        const clearPolling = () => {
            if (intervalRef.current !== null) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
        const startPolling = () => {
            clearPolling();
            intervalRef.current = setInterval(stableCallback, intervalMs);
        };
        const resetIdleTimer = () => {
            if (idleTimerRef.current !== null) {
                clearTimeout(idleTimerRef.current);
                idleTimerRef.current = null;
            }
        };
        const scheduleIdleTimeout = () => {
            if (idleTimeoutMs <= 0)
                return;
            resetIdleTimer();
            idleTimerRef.current = setTimeout(() => {
                isIdleRef.current = true;
                clearPolling();
            }, idleTimeoutMs);
        };
        const onActivity = () => {
            if (document.hidden)
                return;
            const now = Date.now();
            if (now - lastActivityTimeRef.current < THROTTLE_MS)
                return;
            lastActivityTimeRef.current = now;
            if (isIdleRef.current) {
                isIdleRef.current = false;
                stableCallback();
                startPolling();
            }
            scheduleIdleTimeout();
        };
        const onVisibilityChange = () => {
            if (document.hidden) {
                clearPolling();
                resetIdleTimer();
            }
            else {
                isIdleRef.current = false;
                stableCallback();
                startPolling();
                scheduleIdleTimeout();
            }
        };
        if (!document.hidden) {
            startPolling();
            scheduleIdleTimeout();
        }
        document.addEventListener('visibilitychange', onVisibilityChange);
        for (const event of ACTIVITY_EVENTS) {
            document.addEventListener(event, onActivity, { passive: true });
        }
        return () => {
            clearPolling();
            resetIdleTimer();
            document.removeEventListener('visibilitychange', onVisibilityChange);
            for (const event of ACTIVITY_EVENTS) {
                document.removeEventListener(event, onActivity);
            }
        };
    }, [intervalMs, idleTimeoutMs, stableCallback]);
}
